// frontend/components/settings/UserNotifySection.tsx

"use client";

import { useState } from "react";
import { Bell, RefreshCw, Send } from "lucide-react";
import { API_URL, authHeaders } from "../../utils/api";

interface UserNotifySectionProps {
  accessToken?: string;
}

type NotifyType = "POSITION_CHANGE" | "PASSWORD_RESET";

interface BtnState {
  loading: boolean;
  sent: boolean;
  error: string;
}

const INIT: BtnState = { loading: false, sent: false, error: "" };

export default function UserNotifySection({ accessToken }: UserNotifySectionProps) {
  const [posState, setPosState] = useState<BtnState>(INIT);
  const [pwState, setPwState]   = useState<BtnState>(INIT);

  const sendRequest = async (type: NotifyType) => {
    const setState = type === "POSITION_CHANGE" ? setPosState : setPwState;
    setState({ loading: true, sent: false, error: "" });

    try {
      const res = await fetch(`${API_URL}/system/notify`, {
        method: "POST",
        headers: authHeaders(accessToken),
        body: JSON.stringify({ type }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setState({ loading: false, sent: false, error: data.error || "요청 전송에 실패했습니다." });
      } else {
        setState({ loading: false, sent: true, error: "" });
      }
    } catch {
      setState({ loading: false, sent: false, error: "네트워크 오류가 발생했습니다." });
    }
  };

  const btnCls = "flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors";
  const activeCls = `${btnCls} border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30`;
  const sentCls   = `${btnCls} border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 cursor-default`;
  const disabledCls = `${btnCls} border-gray-200 dark:border-zinc-700 text-gray-400 dark:text-zinc-500 cursor-not-allowed`;

  return (
    <div className="pt-4 border-t border-gray-100 dark:border-zinc-800 space-y-4">
      <p className="text-xs font-semibold text-gray-400 dark:text-zinc-500 tracking-wide">관리자에게 요청하기</p>

      {/* 직급·소속 변경 요청 */}
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0 w-7 h-7 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
          <Bell size={13} className="text-orange-500 dark:text-orange-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-700 dark:text-zinc-200 mb-0.5">직급·소속 변경 요청</p>
          <p className="text-xs text-gray-400 dark:text-zinc-500 mb-2">
            직급이 변경되었거나 소속 부서가 달라진 경우 관리자에게 알림을 보냅니다.
          </p>
          <button
            disabled={posState.loading || posState.sent}
            onClick={() => sendRequest("POSITION_CHANGE")}
            className={posState.sent ? sentCls : posState.loading ? disabledCls : activeCls}
          >
            <Send size={13} />
            {posState.loading ? "전송 중…" : posState.sent ? "요청 완료" : "변경 요청 보내기"}
          </button>
          {posState.sent && (
            <p className="mt-1.5 text-xs text-green-600 dark:text-green-400">
              관리자에게 알림이 전송되었습니다.
            </p>
          )}
          {posState.error && (
            <p className="mt-1.5 text-xs text-red-500 dark:text-red-400">{posState.error}</p>
          )}
        </div>
      </div>

      {/* 비밀번호 초기화 요청 */}
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0 w-7 h-7 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
          <RefreshCw size={13} className="text-purple-500 dark:text-purple-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-700 dark:text-zinc-200 mb-0.5">비밀번호 초기화 요청</p>
          <p className="text-xs text-gray-400 dark:text-zinc-500 mb-2">
            비밀번호를 잊어버렸거나 변경이 필요하다면 관리자에게 초기화를 요청합니다.
          </p>
          <button
            disabled={pwState.loading || pwState.sent}
            onClick={() => sendRequest("PASSWORD_RESET")}
            className={pwState.sent ? sentCls : pwState.loading ? disabledCls : activeCls}
          >
            <Send size={13} />
            {pwState.loading ? "전송 중…" : pwState.sent ? "요청 완료" : "초기화 요청 보내기"}
          </button>
          {pwState.sent && (
            <p className="mt-1.5 text-xs text-green-600 dark:text-green-400">
              관리자에게 알림이 전송되었습니다.
            </p>
          )}
          {pwState.error && (
            <p className="mt-1.5 text-xs text-red-500 dark:text-red-400">{pwState.error}</p>
          )}
        </div>
      </div>
    </div>
  );
}
